import { AdvancedSelect } from '../../../../../widgets/select';
import { Dialog, useDialogManager } from '../../../../../widgets/dialog';
import { FormControl } from '../../../../../widgets/formControl';
import { Input } from '../../../../../widgets/input';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import LightIcon from 'src/shared/material_icons/action/lightbulb.svg';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import NewIcon from '../icons/addIcon.svg';
import CloneIcon from '../icons/cloneIcon.svg';
import RemoveIcon from '../icons/removeIcon.svg';
import RenameIcon from '../icons/renameIcon.svg';
import { ToolGrid } from '../toolsgrid';
import TreeComponent, { TreeHandle } from '../TreeComponent';
import LightsOverlayComponent from './overlay/LightsOverlayComponent';
import { BaseTreeNode, useTreeNodeManager } from './hooks/useTreeNodeManager';

// Utilities per conversione colori (pattern MaterialOverlayComponent)
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const cleanHex = hex.replace('#', '');
    if (cleanHex.length !== 6) return null;

    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    return { r, g, b };
};

const rgbToHex = (r: number, g: number, b: number): string => {
    const clamp = (val: number) => Math.max(0, Math.min(255, Math.round(val * 255)));
    return (
        '#' +
        [clamp(r), clamp(g), clamp(b)]
            .map((x) => x.toString(16).padStart(2, '0'))
            .join('')
            .toUpperCase()
    );
};

// LightNode interface semplificata seguendo il pattern di ObjectEditor
export interface LightNode extends BaseTreeNode {
    type: 'light' | 'group';
    lightType: string;
    intensity: number;
    diffuseColor: string;
    specularColor: string;
    customIcon: React.ReactNode;
    notTurnableIntoFolder?: boolean;
    visible?: boolean;
    // Proprietà opzionali per diversi tipi di luci
    groundColor?: string;
    position?: [number, number, number];
    direction?: [number, number, number];
    angle?: number;
    exponent?: number;
    // Proprietà per integrazione SceneEditor
    data: {
        // L'oggetto luce BABYLON completo per accesso diretto
        light?: any;
        // Proprietà scomposte per compatibilità
        lightType?: string;
        intensity?: number;
        color?: string;
        position?: { x: number; y: number; z: number };
        direction?: { x: number; y: number; z: number };
        range?: number;
        angle?: number;
        exponent?: number;
        _internalId?: string;
        enabled?: boolean;
        visible?: boolean;
    };
}

interface LightEditorProps {
    treeRef: React.RefObject<TreeHandle>;
    lights?: any[];
    selectedLights?: any[];
    sceneEditor?: any;
    onLightsUpdated?: (lights: any[]) => void;
}

const LightEditor: FC<LightEditorProps> = ({ treeRef, lights = [], selectedLights = [], sceneEditor, onLightsUpdated }) => {
    const { showDialog, closeDialog } = useDialogManager();

    // State per il refresh trigger (come objectEditor)
    const [lightRefreshTrigger, setLightRefreshTrigger] = useState(false);

    // State locale per le luci (come objectEditor con meshes)
    const [localLights, setLocalLights] = useState<any[]>(() => {
        const initialLights = sceneEditor?.getAllLights() || lights || [];
        console.log('💡 LightEditor: Initial lights loaded:', {
            count: initialLights.length,
            sceneEditorExists: !!sceneEditor,
            fromSceneEditor: !!sceneEditor?.getAllLights()?.length,
            fromProps: lights.length
        });
        return initialLights;
    });

    // Ref per tenere i nodi correnti durante l'update
    const currentNodesRef = useRef<LightNode[]>([]);

    // Converti le luci dal SceneEditor in LightNode per il tree
    const convertToLightNodes = useCallback((sceneLights: any[]): LightNode[] => {
        if (!sceneLights || sceneLights.length === 0) {
            return [];
        }

        return sceneLights.map((light: any, index: number) => {
            // Assicurati che la luce abbia metadata
            if (!light.metadata) {
                light.metadata = {};
            }
            if (light.metadata.displayOrder === undefined) {
                light.metadata.displayOrder = index;
            }

            // Determina il tipo di luce
            let lightType = 'point_light';
            if (light.getTypeID) {
                const typeId = light.getTypeID();
                switch (typeId) {
                    case 0:
                        lightType = 'point_light';
                        break;
                    case 1:
                        lightType = 'directional_light';
                        break;
                    case 2:
                        lightType = 'spot_light';
                        break;
                    case 3:
                        lightType = 'hemispheric_light';
                        break;
                }
            }

            // Converti i colori BABYLON in hex
            const getDiffuseColor = () => {
                if (light.diffuse) {
                    return rgbToHex(light.diffuse.r, light.diffuse.g, light.diffuse.b);
                }
                return '#FFFFFF';
            };

            const getSpecularColor = () => {
                if (light.specular) {
                    return rgbToHex(light.specular.r, light.specular.g, light.specular.b);
                }
                return '#FFFFFF';
            };

            const getGroundColor = () => {
                if (light.groundColor) {
                    return rgbToHex(light.groundColor.r, light.groundColor.g, light.groundColor.b);
                }
                return '#000000';
            };

            return {
                id: light._internalId || light.id || `light-${index}`,
                name: light.name || `Light ${index + 1}`,
                type: 'light' as const,
                lightType: lightType,
                intensity: light.intensity ?? 1.0,
                diffuseColor: getDiffuseColor(),
                specularColor: getSpecularColor(),
                children: [],
                customIcon: <LightIcon style={{ width: '16px', height: '16px', fill: '#ffc107' }} />,
                notTurnableIntoFolder: true,
                visible: light.isEnabled ? light.isEnabled() : true,
                position: light.position ? [light.position.x, light.position.y, light.position.z] : [0, 0, 0],
                direction: light.direction ? [light.direction.x, light.direction.y, light.direction.z] : [0, -1, 0],
                angle: light.angle ? (light.angle * 180) / Math.PI : 30, // Convertito in gradi per UI
                exponent: light.exponent ?? 1,
                groundColor: getGroundColor(),
                data: {
                    // Salva l'oggetto luce completo per accesso diretto
                    light: light,
                    lightType: lightType,
                    intensity: light.intensity ?? 1.0,
                    color: getDiffuseColor(), // Usa il colore convertito
                    position: light.position ? { x: light.position.x, y: light.position.y, z: light.position.z } : { x: 0, y: 0, z: 0 },
                    direction: light.direction ? { x: light.direction.x, y: light.direction.y, z: light.direction.z } : { x: 0, y: -1, z: 0 },
                    range: light.range ?? 100,
                    angle: light.angle ? (light.angle * 180) / Math.PI : 30, // Convertito in gradi
                    exponent: light.exponent ?? 1,
                    _internalId: light._internalId,
                    enabled: light.isEnabled ? light.isEnabled() : true,
                    visible: light.isEnabled ? light.isEnabled() : true
                }
            };
        });
    }, []);

    // Ottieni le luci iniziali convertite - USA QUELLI DALLO SCENE EDITOR
    const lightNodes = useMemo(() => {
        console.log('💡 Recalculating lightNodes with localLights:', localLights.length);
        const nodes = convertToLightNodes(localLights);
        console.log('💡 Converted nodes:', nodes.length);
        // Aggiorna il ref per usarlo durante l'update
        currentNodesRef.current = nodes;
        return nodes;
    }, [localLights, convertToLightNodes]);

    // Ref per tenere riferimento alla funzione updateNode
    const updateNodeRef = useRef<((id: string, updates: Partial<LightNode>) => void) | null>(null);

    // Funzione per creare il componente overlay
    const createLightOverlay = useCallback((node: LightNode) => {
        return () => {
            // I nodi gruppo non hanno overlay
            if (node.type === 'group') {
                return null;
            }

            // Usa la luce BABYLON reale direttamente
            const light = node.data.light;

            if (!light) {
                return <div>Light not found</div>;
            }

            // Callback per aggiornare la luce - pattern ObjectOverlayComponent completo
            const handleLightUpdate = (updates: any) => {
                console.log('💡 handleLightUpdate called with:', updates);

                // 1. Aggiorna la luce BABYLON direttamente
                if (light) {
                    // Nome
                    if (updates.name && light.name !== updates.name) {
                        light.name = updates.name;
                        console.log('💡 Updated BABYLON light name to:', updates.name);
                    }

                    // Intensità
                    if (updates.intensity !== undefined && light.intensity !== updates.intensity) {
                        light.intensity = updates.intensity;
                        console.log('💡 Updated BABYLON light intensity to:', updates.intensity);
                    }

                    // Posizione (per point e spot lights)
                    if (updates.position && light.position) {
                        light.position.x = updates.position[0];
                        light.position.y = updates.position[1];
                        light.position.z = updates.position[2];
                        console.log('💡 Updated BABYLON light position to:', updates.position);
                    }

                    // Direzione (per directional e spot lights)
                    if (updates.direction && light.direction) {
                        light.direction.x = updates.direction[0];
                        light.direction.y = updates.direction[1];
                        light.direction.z = updates.direction[2];
                        console.log('💡 Updated BABYLON light direction to:', updates.direction);
                    }

                    // Colori (diffuse/specular) - convertiti da hex a Vector3
                    if (updates.diffuseColor && light.diffuse) {
                        const rgb = hexToRgb(updates.diffuseColor);
                        if (rgb) {
                            light.diffuse.r = rgb.r / 255;
                            light.diffuse.g = rgb.g / 255;
                            light.diffuse.b = rgb.b / 255;
                            console.log('💡 Updated BABYLON light diffuse color to:', updates.diffuseColor);
                        }
                    }

                    if (updates.specularColor && light.specular) {
                        const rgb = hexToRgb(updates.specularColor);
                        if (rgb) {
                            light.specular.r = rgb.r / 255;
                            light.specular.g = rgb.g / 255;
                            light.specular.b = rgb.b / 255;
                            console.log('💡 Updated BABYLON light specular color to:', updates.specularColor);
                        }
                    }

                    // Ground color per hemispheric lights
                    if (updates.groundColor && light.groundColor) {
                        const rgb = hexToRgb(updates.groundColor);
                        if (rgb) {
                            light.groundColor.r = rgb.r / 255;
                            light.groundColor.g = rgb.g / 255;
                            light.groundColor.b = rgb.b / 255;
                            console.log('💡 Updated BABYLON light ground color to:', updates.groundColor);
                        }
                    }

                    // Angolo per spot lights (convertito da gradi a radianti)
                    if (updates.angle !== undefined && light.angle !== undefined) {
                        light.angle = (updates.angle * Math.PI) / 180;
                        console.log('💡 Updated BABYLON light angle to:', updates.angle, 'degrees');
                    }

                    // Esponente per spot lights
                    if (updates.exponent !== undefined && light.exponent !== undefined) {
                        light.exponent = updates.exponent;
                        console.log('💡 Updated BABYLON light exponent to:', updates.exponent);
                    }
                }

                // 2. Aggiorna il nodo nel tree (per sincronizzare nome visibile e altri valori)
                if (updateNodeRef.current) {
                    const nodeUpdates: any = {};

                    if (updates.name) {
                        nodeUpdates.name = updates.name;
                    }
                    if (updates.intensity !== undefined) {
                        nodeUpdates.intensity = updates.intensity;
                    }
                    if (updates.diffuseColor) {
                        nodeUpdates.diffuseColor = updates.diffuseColor;
                    }
                    if (updates.specularColor) {
                        nodeUpdates.specularColor = updates.specularColor;
                    }
                    if (updates.groundColor) {
                        nodeUpdates.groundColor = updates.groundColor;
                    }
                    if (updates.position) {
                        nodeUpdates.position = updates.position;
                    }
                    if (updates.direction) {
                        nodeUpdates.direction = updates.direction;
                    }
                    if (updates.angle !== undefined) {
                        nodeUpdates.angle = updates.angle;
                    }
                    if (updates.exponent !== undefined) {
                        nodeUpdates.exponent = updates.exponent;
                    }

                    if (Object.keys(nodeUpdates).length > 0) {
                        console.log('💡 Updating tree node with:', nodeUpdates);
                        updateNodeRef.current(node.id, nodeUpdates);
                    }
                }

                // 3. Renderizza la scena per mostrare i cambiamenti
                if (sceneEditor && sceneEditor.render) {
                    console.log('💡 Calling sceneEditor.render()');
                    sceneEditor.render();
                }
            };

            // Prepara i dati della luce per l'overlay - usa i valori dal nodo che sono già convertiti
            const lightData = {
                id: light._internalId || light.id,
                name: light.name,
                lightType: node.lightType,
                intensity: light.intensity ?? node.intensity,
                diffuseColor: node.diffuseColor, // Già convertito in hex da convertToLightNodes
                specularColor: node.specularColor, // Già convertito in hex da convertToLightNodes
                groundColor: node.groundColor, // Già convertito in hex da convertToLightNodes
                position: light.position ? ([light.position.x, light.position.y, light.position.z] as [number, number, number]) : node.position,
                direction: light.direction ? ([light.direction.x, light.direction.y, light.direction.z] as [number, number, number]) : node.direction,
                angle: light.angle ? (light.angle * 180) / Math.PI : node.angle, // Convertito in gradi per UI
                exponent: light.exponent ?? node.exponent
            };

            return <LightsOverlayComponent light={lightData} onUpdate={handleLightUpdate} />;
        };
    }, []);

    const {
        nodes,
        selectedIds,
        treeState,
        filter,
        setFilter,
        findNodeById,
        updateNode,
        cloneSelectedNodes,
        removeNodes,
        groupSelectedNodes,
        setNodes,
        filteredData
    } = useTreeNodeManager<LightNode>(lightNodes, createLightOverlay);

    // Aggiungi il componente overlay direttamente ai nodi del treeState (come objectEditor)
    useEffect(() => {
        treeState.nodes.forEach((node) => {
            if (!node.component) {
                node.component = createLightOverlay(node);
            }
        });
    }, [treeState.nodes, createLightOverlay]);

    // Aggiorna il ref con la funzione updateNode una volta disponibile
    useEffect(() => {
        updateNodeRef.current = updateNode;
    }, [updateNode]);

    // useEffect per gestire il lightRefreshTrigger (come objectEditor)
    useEffect(() => {
        if (lightRefreshTrigger && sceneEditor) {
            console.log('💡 lightRefreshTrigger activated - getting fresh lights');
            const newLights = sceneEditor.getAllLights();
            console.log('💡 Fresh lights count:', newLights.length);
            setLocalLights(newLights);
            setLightRefreshTrigger(false);
        }
    }, [lightRefreshTrigger, sceneEditor]);

    // Callback per aggiornare le luci nel SceneEditor quando il tree cambia
    const handleTreeDataChanged = useCallback(
        (updatedNodes: LightNode[]) => {
            if (onLightsUpdated) {
                const sceneLights = updatedNodes.map((node) => node.data.light).filter(Boolean);
                onLightsUpdated(sceneLights);
            }
        },
        [onLightsUpdated]
    );

    // Funzione per forzare il refresh del tree dalle luci (come prepareMaterials in materialsEditor)
    const prepareLights = useCallback(() => {
        console.log('💡 Preparing lights after operation...');
        if (sceneEditor) {
            console.log('💡 Getting updated lights from SceneEditor');
            // USA IL PATTERN ESATTO DI OBJECTEDITOR: setTrigger + getAll + notify
            setLightRefreshTrigger(true);
            const updatedLights = sceneEditor.getAllLights();
            console.log('💡 Updated lights count:', updatedLights.length);

            // Notifica il componente padre con le luci aggiornate
            if (onLightsUpdated) {
                onLightsUpdated(updatedLights);
            }
        }
    }, [sceneEditor, onLightsUpdated]);

    // Crea un treeState per il TreeComponent che usa filteredData quando c'è un filtro attivo
    const treeStateForComponent = useMemo(() => {
        const sourceNodes = filter ? filteredData : treeState.nodes;
        return {
            ...treeState,
            nodes: sourceNodes
        };
    }, [treeState, filteredData, filter]);

    // Struttura dati standard per nuove luci usando SceneEditor
    const createNewLight = useCallback(
        (name: string, lightType: string = 'point_light'): LightNode => {
            console.log('💡 Creating new light:', name, lightType);

            // Se abbiamo SceneEditor, usa quello per creare la luce
            let newLight: any | null = null;
            if (sceneEditor && sceneEditor.createLight) {
                // Mappa i tipi di luce ai valori BABYLON
                const babylonLightType = lightType.replace('_light', '').replace('_', ''); // point_light -> point
                newLight = sceneEditor.createLight(name, babylonLightType);
                console.log('💡 Created BABYLON light:', newLight);
            }

            const lightId = newLight?.id || newLight?._internalId || `light-${Date.now()}`;

            // Crea la struttura base del nodo
            const baseLight: LightNode = {
                id: lightId,
                name: newLight?.name || name,
                type: 'light',
                lightType,
                intensity: newLight?.intensity || 1,
                diffuseColor: '#FFFFFF',
                specularColor: '#FFFFFF',
                children: [],
                customIcon: <LightIcon style={{ width: '16px', height: '16px', fill: '#ffc107' }} />,
                visible: true,
                data: {
                    light: newLight,
                    lightType: lightType,
                    intensity: newLight?.intensity || 1,
                    color: '#FFFFFF',
                    enabled: true,
                    visible: true,
                    _internalId: newLight?._internalId
                }
            };

            // Aggiungi proprietà specifiche per tipo di luce
            switch (lightType) {
                case 'point_light':
                    return {
                        ...baseLight,
                        position: newLight?.position ? [newLight.position.x, newLight.position.y, newLight.position.z] : [0, 10, 0],
                        data: {
                            ...baseLight.data,
                            position: newLight?.position || { x: 0, y: 10, z: 0 }
                        }
                    };
                case 'spot_light':
                    return {
                        ...baseLight,
                        position: newLight?.position ? [newLight.position.x, newLight.position.y, newLight.position.z] : [0, 10, 0],
                        direction: newLight?.direction ? [newLight.direction.x, newLight.direction.y, newLight.direction.z] : [0, -1, 0],
                        exponent: newLight?.exponent || 1,
                        angle: newLight?.angle ? (newLight.angle * 180) / Math.PI : 30,
                        data: {
                            ...baseLight.data,
                            position: newLight?.position || { x: 0, y: 10, z: 0 },
                            direction: newLight?.direction || { x: 0, y: -1, z: 0 },
                            angle: newLight?.angle || Math.PI / 6,
                            exponent: newLight?.exponent || 1
                        }
                    };
                case 'directional_light':
                    return {
                        ...baseLight,
                        direction: newLight?.direction ? [newLight.direction.x, newLight.direction.y, newLight.direction.z] : [0, -1, 0],
                        data: {
                            ...baseLight.data,
                            direction: newLight?.direction || { x: 0, y: -1, z: 0 }
                        }
                    };
                case 'hemispheric_light':
                    return {
                        ...baseLight,
                        groundColor: '#000000'
                    };
                default:
                    return baseLight;
            }
        },
        [sceneEditor]
    );

    // Opzioni per i tipi di luce con descrizioni
    const lightsOptions: Array<{ value: string; label: string; description: string }> = [
        { value: 'point_light', label: 'Point Light', description: 'Emits light in all directions from a single point' },
        { value: 'spot_light', label: 'Spot Light', description: 'Emits light in a cone shape with position and direction' },
        { value: 'directional_light', label: 'Directional Light', description: 'Emits parallel light rays (like sunlight)' },
        { value: 'hemispheric_light', label: 'Hemispheric Light', description: 'Ambient light with sky and ground colors' }
    ];

    const handleNewLight = () => {
        treeState.closeOverlay();

        // Componente interno per gestire lo state del form
        const NewLightDialog = () => {
            const validationContext = useValidationContext();
            const [lightName, setLightName] = useState('');
            const [selectedLightType, setSelectedLightType] = useState(lightsOptions[0]);

            const handleCreate = () => {
                if (!validationContext.submit()) return;

                console.log('💡 Creating new light:', lightName, 'type:', selectedLightType.value);

                // CORREZIONE: Prima crea la luce BABYLON, poi aggiorna dal SceneEditor
                if (sceneEditor && sceneEditor.createLight) {
                    // Mappa i tipi di luce ai valori BABYLON con la prima lettera maiuscola
                    const lightTypeParts = selectedLightType.value.replace('_light', '').split('_');
                    const babylonLightType = lightTypeParts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join('');
                    console.log('💡 Light type mapping:', selectedLightType.value, '->', babylonLightType);

                    const createdLight = sceneEditor.createLight(lightName.trim(), babylonLightType);
                    console.log('💡 Created BABYLON light:', createdLight);
                }

                // Aggiorna dal SceneEditor che ora contiene la nuova luce
                prepareLights();
                closeDialog('new-light-dialog');
            };

            return (
                <Dialog
                    title='Create New Light'
                    buttons={[
                        {
                            label: 'Create',
                            onClick: handleCreate
                        }
                    ]}
                >
                    <FormControl label='Light Name'>
                        <Input
                            placeholder='Type light name...'
                            fullWidth
                            value={lightName}
                            onChange={(e) => setLightName(e.target.value)}
                            validationKey='newLightName'
                            validationLabel={T._('Light Name', 'Admin')}
                            validate={validateRequiredTrimmed}
                            validationMessage={T._('Light Name is required.', 'Admin')}
                        />
                    </FormControl>
                    <FormControl label='Light Type'>
                        <AdvancedSelect
                            isClearable={false}
                            isSearchable={false}
                            options={lightsOptions}
                            value={selectedLightType}
                            onChange={(selectedOption) => {
                                if (selectedOption) {
                                    setSelectedLightType(selectedOption);
                                }
                            }}
                            placeholder='Select light type...'
                        />
                        <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic', marginTop: '4px' }}>{selectedLightType.description}</div>
                    </FormControl>
                </Dialog>
            );
        };

        showDialog(
            'new-light-dialog',
            <ValidationStateProvider>
                <NewLightDialog />
            </ValidationStateProvider>
        );
    };

    const tools = [
        {
            name: 'NEW LIGHT',
            tooltip: 'Crea nuova luce',
            icon: <NewIcon />,
            onClick: () => {
                treeState.closeOverlay();
                handleNewLight();
            }
        },
        {
            name: 'CLONE',
            tooltip: 'Clona elementi selezionati',
            icon: <CloneIcon />,
            disabled: selectedIds.length === 0,
            onClick: () => {
                if (!selectedIds.length) return;
                treeState.closeOverlay();

                selectedIds.forEach((selectedId) => {
                    const selectedNode = findNodeById(selectedId);
                    if (selectedNode && selectedNode.type === 'light') {
                        const clonedName = `${selectedNode.name}-copy`;

                        // Se abbiamo SceneEditor e la luce originale
                        if (sceneEditor && sceneEditor.cloneLight && selectedNode.data?.light?.id) {
                            console.log('💡 Cloning light via SceneEditor:', selectedNode.data.light.id);
                            const clonedLight = sceneEditor.cloneLight(selectedNode.data.light.id, undefined, clonedName);
                            console.log('💡 Cloned light created:', clonedLight);
                        }
                    }
                });

                // CORREZIONE: Aggiorna dal SceneEditor che ora contiene le luci clonate
                prepareLights();
            }
        },
        {
            name: 'RENAME',
            tooltip: 'Rinomina elemento selezionato',
            icon: <RenameIcon />,
            disabled: selectedIds.length !== 1,
            onClick: () => {
                if (selectedIds.length !== 1) return;
                treeState.closeOverlay();

                const selectedNode = findNodeById(Array.from(selectedIds)[0]);
                if (!selectedNode) return;

                const currentName = selectedNode.name;
                const RenameLightDialog = () => {
                    const validationContext = useValidationContext();
                    const [name, setName] = useState(currentName);

                    const handleRename = () => {
                        if (!validationContext.submit()) return;

                        const newName = name.trim();
                        if (newName === currentName) {
                            closeDialog('rename-dialog');
                            return;
                        }

                        console.log('💡 Renaming light:', selectedNode.id, 'from', currentName, 'to', newName);

                        // Se abbiamo SceneEditor, usa quello per rinominare
                        if (sceneEditor && sceneEditor.renameLight && selectedNode.data?.light?.id) {
                            sceneEditor.renameLight(selectedNode.data.light.id, newName);
                        }

                        // Aggiorna il nodo nel tree
                        selectedNode.name = newName;
                        if (selectedNode.data.light) {
                            selectedNode.data.light.name = newName;
                        }

                        // Aggiorna il tree
                        handleTreeDataChanged(nodes);

                        // USA IL PATTERN DI OBJECTEDITOR: prepareLights()
                        prepareLights();

                        closeDialog('rename-dialog');
                    };

                    return (
                        <Dialog
                            title='Rename Light'
                            buttons={[
                                {
                                    label: 'OK',
                                    onClick: handleRename
                                }
                            ]}
                        >
                            <FormControl label='Light Name'>
                                <Input
                                    value={name}
                                    placeholder='Enter light name...'
                                    fullWidth
                                    onChange={(e) => setName(e.target.value)}
                                    validationKey='renameLightName'
                                    validationLabel={T._('Light Name', 'Admin')}
                                    validate={validateRequiredTrimmed}
                                    validationMessage={T._('Light Name is required.', 'Admin')}
                                />
                            </FormControl>
                        </Dialog>
                    );
                };

                showDialog(
                    'rename-dialog',
                    <ValidationStateProvider>
                        <RenameLightDialog />
                    </ValidationStateProvider>
                );
            }
        },
        {
            name: 'REMOVE',
            tooltip: 'Rimuovi elemento selezionato',
            icon: <RemoveIcon />,
            disabled: selectedIds.length === 0,
            onClick: () => {
                if (!selectedIds.length) return;
                treeState.closeOverlay();

                showDialog(
                    'remove-dialog',
                    <Dialog
                        title='Remove Light'
                        showCloseButton={false}
                        buttons={[
                            {
                                label: 'Cancel',
                                onClick: () => closeDialog('remove-dialog')
                            },
                            {
                                label: 'Remove',
                                onClick: () => {
                                    console.log('💡 Removing lights:', Array.from(selectedIds));

                                    // Se abbiamo SceneEditor, rimuovi anche da lì
                                    if (sceneEditor && sceneEditor.deleteLight) {
                                        selectedIds.forEach((selectedId) => {
                                            const selectedNode = findNodeById(selectedId);
                                            if (selectedNode && selectedNode.data?.light?.id) {
                                                console.log('💡 Deleting light from SceneEditor:', selectedNode.data.light.id);
                                                sceneEditor.deleteLight(selectedNode.data.light.id);
                                            }
                                        });
                                    }

                                    // Rimuovi dal tree state
                                    removeNodes(selectedIds);

                                    // USA IL PATTERN DI OBJECTEDITOR: prepareLights()
                                    prepareLights();

                                    closeDialog('remove-dialog');
                                }
                            }
                        ]}
                    >
                        <p>Are you sure you want to remove the selected light(s)? This action cannot be undone.</p>
                    </Dialog>
                );
            }
        }
    ];
    console.log('treeStateForComponent', treeStateForComponent);
    return (
        <div>
            <ToolGrid tools={tools} columns={tools.length} />
            <TreeComponent
                ref={treeRef}
                treeState={treeStateForComponent}
                filter={filter}
                setFilter={setFilter}
                showVisibilityIcon={false}
                onNodeMoved={(nodeId, targetParentId, index) => {
                    console.log(`Node ${nodeId} moved to parent ${targetParentId} at index ${index}`);

                    // Determina quali nodi spostare
                    const nodesToMove = treeState.selectedIds.has(nodeId) ? Array.from(treeState.selectedIds) : [nodeId];

                    // Usa la funzione dall'hook
                    treeState.moveSelection(nodesToMove, targetParentId);

                    // Se abbiamo SceneEditor, notifica il cambiamento
                    if (onLightsUpdated && sceneEditor) {
                        const allLights = sceneEditor.getAllLights();
                        onLightsUpdated(allLights);
                    }
                }}
                hideSearchInput={!filter}
            />
        </div>
    );
};

export default LightEditor;
